import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  Box,
  Flex,
  Text,
  IconButton,
  VStack,
  Tooltip,
  Divider,
  Badge,
} from '@chakra-ui/react';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@chakra-ui/icons';
import { fetchWithAuth } from '../../utils/fetchWithAuth';

const AdminLayout = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [intentCount, setIntentCount] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchIntentCount = async () => {
      try {
        const res = await fetchWithAuth('/api/subscription/admin/upgrade-intents');
        if (res.ok) {
          const intents = await res.json();
          // Count unique users with pending intents (not yet Pro)
          const pendingIntents = intents.filter(i => i.plan_type !== 'pro');
          const uniqueUsers = new Set(pendingIntents.map(i => i.user_id)).size;
          setIntentCount(uniqueUsers);
        }
      } catch (error) {
        console.error('Error fetching intent count:', error);
      }
    };
    fetchIntentCount();
  }, []);

  const navItems = [
    { to: '/admin', label: 'Dashboard', icon: 'dashboard', end: true },
    { to: '/admin/pilot', label: 'Pilot Tracker', icon: 'science' },
    { to: '/admin/users', label: 'Users', icon: 'people' },
    { to: '/admin/credits', label: 'Credits', icon: 'toll' },
    { to: '/admin/agent-activity', label: 'Agent Activity', icon: 'bolt' },
    { to: '/admin/token-usage', label: 'Token Usage', icon: 'bar_chart' },
    { to: '/admin/system', label: 'System', icon: 'settings' },
  ];

  return (
    <Flex minH="100vh" bg="gray.50">
      <Box
        as="nav"
        w={isExpanded ? '220px' : '60px'}
        bg="gray.900"
        color="white"
        transition="width 0.2s ease"
        position="fixed"
        left={0}
        top={0}
        h="100vh"
        zIndex={100}
      >
        <Flex
          h="56px"
          align="center"
          justify={isExpanded ? 'space-between' : 'center'}
          px={isExpanded ? 4 : 0}
          borderBottom="1px solid"
          borderColor="gray.700"
        >
          {isExpanded && (
            <Text
              fontSize="lg"
              fontWeight="bold"
              cursor="pointer"
              onClick={() => navigate('/admin')}
            >
              Admin
            </Text>
          )}
          <IconButton
            icon={isExpanded ? <ChevronLeftIcon /> : <ChevronRightIcon />}
            aria-label="Toggle sidebar"
            variant="ghost"
            color="white"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            _hover={{ bg: 'gray.700' }}
          />
        </Flex>

        <VStack spacing={1} align="stretch" py={4}>
          {navItems.map((item) => (
            <Tooltip
              key={item.to}
              label={item.label}
              placement="right"
              isDisabled={isExpanded}
            >
              <Box>
                <NavLink
                  to={item.to}
                  end={item.end}
                  style={({ isActive }) => ({
                    textDecoration: 'none',
                  })}
                >
                  {({ isActive }) => (
                    <Flex
                      align="center"
                      px={isExpanded ? 4 : 0}
                      py={2}
                      mx={2}
                      borderRadius="md"
                      bg={isActive ? 'blue.600' : 'transparent'}
                      _hover={{ bg: isActive ? 'blue.600' : 'gray.700' }}
                      justify={isExpanded ? 'flex-start' : 'center'}
                      cursor="pointer"
                    >
                      <Text
                        className="material-icons-outlined"
                        fontSize="20px"
                        mr={isExpanded ? 3 : 0}
                      >
                        {item.icon}
                      </Text>
                      {isExpanded && (
                        <Flex align="center" flex={1} justify="space-between">
                          <Text fontSize="sm" fontWeight={isActive ? '600' : '400'}>
                            {item.label}
                          </Text>
                          {item.to === '/admin/plans' && intentCount > 0 && (
                            <Badge 
                              colorScheme="green" 
                              fontSize="xs" 
                              borderRadius="full"
                              ml={2}
                            >
                              {intentCount}
                            </Badge>
                          )}
                        </Flex>
                      )}
                    </Flex>
                  )}
                </NavLink>
              </Box>
            </Tooltip>
          ))}
        </VStack>

        <Divider borderColor="gray.700" my={2} />

        <Tooltip label="Back to App" placement="right" isDisabled={isExpanded}>
          <Box>
            <Flex
              align="center"
              px={isExpanded ? 4 : 0}
              py={2}
              mx={2}
              borderRadius="md"
              _hover={{ bg: 'gray.700' }}
              justify={isExpanded ? 'flex-start' : 'center'}
              cursor="pointer"
              onClick={() => navigate('/brief')}
            >
              <Text
                className="material-icons-outlined"
                fontSize="20px"
                mr={isExpanded ? 3 : 0}
              >
                arrow_back
              </Text>
              {isExpanded && (
                <Text fontSize="sm">Back to App</Text>
              )}
            </Flex>
          </Box>
        </Tooltip>
      </Box>

      <Box
        ml={isExpanded ? '220px' : '60px'}
        flex={1}
        minW={0}
        transition="margin-left 0.2s ease"
        p={6}
        minH="100vh"
      >
        <Outlet />
      </Box>
    </Flex>
  );
};

export default AdminLayout;
